import { API_BASE } from '../../apiBase';
import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Sent/Paid date cell — double-click (or double-tap) to edit; empty clears.
// Local-noon save avoids the off-by-a-day timezone trap.
function InvDateCell({ inv, field, onRefresh, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const cur = inv[field] ? new Date(inv[field]) : null;
  const toLocalYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const start = (e) => { e.stopPropagation(); setVal(cur ? toLocalYMD(cur) : ''); setEditing(true); };
  const save = async () => {
    setEditing(false);
    const iso = val ? new Date(`${val}T12:00:00`).toISOString() : null;
    try {
      await axios.put(`${API_BASE}/invoices/${inv._id}`, { [field]: iso },
        { headers: { Authorization: localStorage.getItem('token') } });
      onRefresh && onRefresh();
    } catch (_) { alert('Could not save the date.'); }
  };
  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        className="bg-gray-800 border border-indigo-500/60 rounded px-2 py-1 text-xs text-white outline-none"
      />
    );
  }
  return (
    <span
      onDoubleClick={start}
      title="Double-click to edit this date"
      className={`cursor-pointer ${cur ? '' : 'text-zinc-600'} ${className}`}
    >
      {cur ? cur.toLocaleDateString() : '—'}
    </span>
  );
}

function RevenueTab({
  invoices = [],
  quotes = [],
  payments = [],
  changeOrders = [],
  informalEstimates = [],
  setInformalEstimates,
  onRefresh,
  handleStatusChange,
  handleRightClick,
  projectId
}) {

  const navigate = useNavigate();
  // Click a document to open it in its billing editor, loaded and ready:
  // drafts are editable/saveable, sent ones viewable/resendable — the editor's
  // live PDF preview doubles as the viewer.
  const openInvoice = (inv) => navigate(`/billing/create-invoice?id=${inv._id}`);
  const openQuote = (q) => navigate(`/billing/create-quote?id=${q._id}`);
  // A payment created via Create Receipt links back to its receipt (new rows
  // carry a real id; older rows fall back to the number in the notes).
  const paymentReceiptLink = (pay) => {
    if (pay.receipt) return `/billing/create-receipt?id=${pay.receipt?._id || pay.receipt}`;
    const m = /Receipt Generated: (\S+)/.exec(pay.notes || '');
    return m ? `/billing/create-receipt?number=${encodeURIComponent(m[1])}` : null;
  };
  // A Paid invoice whose payment is already listed as a real payment row (via
  // a receipt) shouldn't ALSO show as an auto "Invoice Payment" row — that
  // displayed the same money twice.
  const paidInvoicesToShow = invoices.filter(
    (inv) => inv.status === 'Paid' && !payments.some((p) => p.invoice === inv.number)
  );

  // ==================== INFORMAL ESTIMATE LOG ====================
  // Pure record of prices quoted over text/phone. Touches NO totals — money
  // only moves through invoices and payments. This is the paper trail.
  const [showIeForm, setShowIeForm] = useState(false);
  const [ieDesc, setIeDesc] = useState('');
  const [ieAmount, setIeAmount] = useState('');
  const [ieNotes, setIeNotes] = useState('');
  const [savingIe, setSavingIe] = useState(false);

  const addInformalEstimate = async () => {
    if (savingIe || !ieDesc.trim() || ieAmount === '') return;
    setSavingIe(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/projects/${projectId}/informal-estimates`,
        { description: ieDesc.trim(), amount: Number(ieAmount), notes: ieNotes.trim() },
        { headers: { Authorization: token } });
      setIeDesc(''); setIeAmount(''); setIeNotes(''); setShowIeForm(false);
      onRefresh && onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not log the estimate.');
    } finally {
      setSavingIe(false);
    }
  };

  // INSTANT: flip locally first, save in the background, roll back on error.
  const patchIeLocal = (id, changes) => {
    setInformalEstimates && setInformalEstimates(prev => prev.map(x => x._id === id ? { ...x, ...changes } : x));
  };
  const patchIeServer = async (id, changes) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_BASE}/informal-estimates/${id}`, changes, { headers: { Authorization: token } });
    } catch (_) {
      alert('Could not save — reverting.');
      onRefresh && onRefresh(); // pull the truth back
    }
  };
  const toggleIeAgreed = (ie) => {
    patchIeLocal(ie._id, { agreed: !ie.agreed });
    patchIeServer(ie._id, { agreed: !ie.agreed });
  };

  const deleteInformalEstimate = async (ie) => {
    if (!window.confirm('Delete this log entry?')) return;
    // Instant removal; server deletes in the background.
    setInformalEstimates && setInformalEstimates(prev => prev.filter(x => x._id !== ie._id));
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/informal-estimates/${ie._id}`, { headers: { Authorization: token } });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete — restoring.');
      onRefresh && onRefresh();
    }
  };

  // ---- Swipe-to-delete for the estimate log's mobile cards (same pattern
  // as the expense cards): swipe left reveals Delete, tap to confirm. ----
  const [swipedKey, setSwipedKey] = useState(null);
  const [swipeState, setSwipeState] = useState({ key: null, dx: 0, active: false });
  const swipeAteClickAt = useRef(0);
  const trackSwipe = (key, e) => {
    if (e.pointerType === 'mouse') return; // touch/pen only
    const sx = e.clientX, sy = e.clientY;
    let decided = null, lastDx = 0;
    const off = () => {
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    const mv = (ev) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        decided = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (decided === 'v') { off(); return; }
      }
      lastDx = Math.max(-100, Math.min(20, dx));
      setSwipeState({ key, dx: Math.min(0, lastDx), active: true });
    };
    const up = () => {
      off();
      setSwipeState({ key: null, dx: 0, active: false });
      if (decided === 'h') {
        swipeAteClickAt.current = Date.now();
        if (lastDx < -45) setSwipedKey(key);
        else setSwipedKey(k => (k === key ? null : k));
      }
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  useEffect(() => {
    if (!swipedKey) return;
    const close = () => setSwipedKey(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [swipedKey]);
  const SwipeWrap = (key, onDelete, children, rounded = '') => {
    const open = swipedKey === key;
    const liveDx = swipeState.key === key ? swipeState.dx : 0;
    const dx = open ? Math.min(-72, liveDx || -72) : Math.min(0, liveDx);
    return (
      <div className={`relative overflow-hidden ${rounded}`}>
        {(open || dx < 0) && (
          <button
            onClick={(e) => { e.stopPropagation(); setSwipedKey(null); onDelete(e); }}
            className="absolute inset-y-0 right-0 w-20 bg-red-600 active:bg-red-500 text-white text-xs font-black uppercase flex items-center justify-center"
          >
            Delete
          </button>
        )}
        <div
          style={{ transform: `translateX(${dx}px)`, transition: swipeState.key === key && swipeState.active ? 'none' : 'transform 0.22s ease' }}
          className="relative bg-gray-900"
        >
          {children}
        </div>
      </div>
    );
  };

  // ---- Inline edit: double-click (desktop) / double-tap (mobile) the
  // Quoted, Amount, or Notes cell to edit in place. Enter/blur saves,
  // Escape cancels, unchanged values never hit the server. ----
  const [ieEdit, setIeEdit] = useState(null); // { id, field, val }
  const ieTapRef = useRef({ key: null, t: 0 });
  const startIeEdit = (ie, field) => setIeEdit({ id: ie._id, field, val: field === 'amount' ? String(ie.amount ?? '') : (ie[field] || '') });
  const handleIeCellTap = (ie, field) => {
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (!coarse) return; // desktop edits via double-click
    const key = `${ie._id}:${field}`;
    const now = Date.now();
    if (ieTapRef.current.key === key && now - ieTapRef.current.t < 400) {
      ieTapRef.current = { key: null, t: 0 };
      startIeEdit(ie, field);
    } else {
      ieTapRef.current = { key, t: now };
    }
  };
  const commitIeEdit = async () => {
    const ed = ieEdit;
    setIeEdit(null);
    if (!ed) return;
    const ie = informalEstimates.find(x => x._id === ed.id);
    if (!ie) return;
    let payload;
    if (ed.field === 'amount') {
      const amt = Number(ed.val);
      if (!isFinite(amt) || amt === Number(ie.amount)) return; // unchanged/invalid = no call
      payload = { amount: amt };
    } else {
      const v = ed.val.trim();
      if (ed.field === 'description' && !v) return; // a note needs a description
      if (v === (ie[ed.field] || '')) return;
      payload = { [ed.field]: v };
    }
    // Instant in the UI; server saves in the background.
    patchIeLocal(ed.id, payload);
    patchIeServer(ed.id, payload);
  };
  const IeCell = (ie, field, display) =>
    ieEdit && ieEdit.id === ie._id && ieEdit.field === field ? (
      <input
        autoFocus
        value={ieEdit.val}
        onChange={(e) => setIeEdit(p => ({ ...p, val: field === 'amount' ? e.target.value.replace(/[^0-9.-]/g, '') : e.target.value }))}
        onKeyDown={(e) => { if (e.key === 'Enter') commitIeEdit(); if (e.key === 'Escape') setIeEdit(null); }}
        onBlur={commitIeEdit}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        inputMode={field === 'amount' ? 'decimal' : undefined}
        className="bg-gray-800 border border-indigo-500/60 rounded px-2 py-1 text-sm text-white outline-none w-full min-w-0"
      />
    ) : display;

  // ---- Fully Paid: a plain boolean, exactly like Agreed. No links, no
  // logic, no effect on any number. Just a fact you write down. ----
  const toggleIeFullyPaid = (ie) => {
    patchIeLocal(ie._id, { fullyPaid: !ie.fullyPaid });
    patchIeServer(ie._id, { fullyPaid: !ie.fullyPaid });
  };
  const IePaidMark = ({ ie }) => (
    <button
      onClick={(e) => { e.stopPropagation(); toggleIeFullyPaid(ie); }}
      title={ie.fullyPaid ? 'Fully paid — click to unmark' : 'Click when the money is all in'}
      className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all ${
        ie.fullyPaid ? 'bg-zinc-700 border-zinc-600 text-zinc-400' : 'border-zinc-600 text-transparent hover:border-indigo-500'
      }`}
    >
      <Check size={14} />
    </button>
  );

  // The record-fact checkmark: filled = client agreed, hollow = not (yet).
  // A fully-paid row goes all-gray — the deal is done, stop drawing the eye.
  const IeAgreedMark = ({ ie }) => (
    <button
      onClick={(e) => { e.stopPropagation(); toggleIeAgreed(ie); }}
      title={ie.agreed ? 'Client agreed — click to unmark' : 'Click when the client agrees'}
      className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all ${
        ie.agreed
          ? (ie.fullyPaid ? 'bg-zinc-700 border-zinc-600 text-zinc-400' : 'bg-emerald-600 border-emerald-500 text-white')
          : 'border-zinc-600 text-transparent hover:border-emerald-500'
      }`}
    >
      <Check size={14} />
    </button>
  );

  // ---- Receipts made for this project (the proof-of-payment paper trail).
  // Self-contained fetch so this card works without threading new props.
  const [receipts, setReceipts] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API_BASE}/receipts?project=${projectId}`, { headers: { Authorization: localStorage.getItem('token') } })
      .then(r => setReceipts(r.data || []))
      .catch(() => {});
  }, [projectId, invoices]);

  // One look for all the header "create" buttons
  const createBtn = 'flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-medium transition-all text-sm';

  // Quote → invoice lineage: an invoice born from a quote carries sourceQuote,
  // so a quote row can show "Invoiced ✓" (and jump to it) instead of offering
  // to bill the same work twice.
  const invoiceForQuote = (q) => invoices.find((i) => String(i.sourceQuote || '') === String(q._id) && i.status !== 'Revised') || invoices.find((i) => String(i.sourceQuote || '') === String(q._id));

  // ==================== LOCAL STATE ====================
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  const [newPayment, setNewPayment] = useState(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return {
      date: `${year}-${month}-${day}`,
      amount: '',
      method: 'Check',
      notes: ''
    };
  });

  const handleAddPayment = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const targetInvoice = invoices.find(inv => inv.total === Number(newPayment.amount));

      const payload = {
        ...newPayment,
        invoiceNumber: targetInvoice ? targetInvoice.number : '—' 
      };

      await axios.post(`${API_BASE}/payments/${projectId}`, payload, {
        headers: { Authorization: token }
      });

      setShowAddPaymentModal(false);
      setNewPayment({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        method: 'Check',
        notes: ''
      });

      onRefresh();
      
    } catch (err) {
      console.error("Failed to add payment:", err);
      alert("Could not save payment.");
    }
  };

  return (
    <div className="space-y-12">

      {/* Invoices Table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center">
          <h2 className="text-xl font-bold">Invoices</h2>
          <button onClick={() => navigate(`/billing/create-invoice?project=${projectId}`)} className={createBtn}>
            <Plus size={16} /> Create Invoice
          </button>
        </div>
        <div className="overflow-x-auto max-md:hidden">
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead className="bg-gray-950/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Sent Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Paid Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-8 py-12 text-center text-zinc-500">No invoices yet</td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr
                    key={inv._id}
                    onClick={() => openInvoice(inv)}
                    title={inv.status === 'Revised' ? 'Superseded by a revision — view only' : (inv.status === 'Draft' ? 'Open draft to edit' : 'Open invoice')}
                    className={`hover:bg-gray-800/50 transition-colors cursor-pointer ${inv.status === 'Revised' ? 'opacity-50 line-through decoration-zinc-600' : ''}`}
                    onContextMenu={(e) => handleRightClick(e, 'invoice', inv._id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{inv.number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                      ${inv.total ? inv.total.toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={inv.status}
                        onChange={(e) => handleStatusChange('invoice', inv._id, e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs font-medium cursor-pointer focus:border-indigo-500 outline-none"
                      >
                        <option value="Draft">Draft</option>
                        <option value="Sent">Sent</option>
                        <option value="Viewed">Viewed</option>
                        <option value="Paid">Paid</option>
                        <option value="Overdue">Overdue</option>
                        <option value="Revised">Revised (void)</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                      <InvDateCell inv={inv} field="sentAt" onRefresh={onRefresh} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                      <InvDateCell inv={inv} field="paidAt" onRefresh={onRefresh} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {inv.status === 'Revised' ? (
                        <span className="text-zinc-700 text-xs">superseded</span>
                      ) : inv.status === 'Paid' ? (
                        receipts.some(r => String(r.invoice?._id || r.invoice) === String(inv._id)) ? (
                          <span className="text-xs text-emerald-400 font-semibold">✓ Made</span>
                        ) : (
                          <button
                            onClick={() => navigate(`/billing/create-receipt?project=${projectId}&invoice=${inv._id}`)}
                            className="text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/35"
                            title="Create a receipt for this paid invoice"
                          >
                            + Receipt
                          </button>
                        )
                      ) : (
                        <span className="text-zinc-700 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Invoices — mobile cards */}
        <div className="md:hidden divide-y divide-gray-800/40">
          {invoices.length === 0 ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm">No invoices yet</p>
          ) : invoices.map((inv) => (
            <div key={inv._id} onClick={() => openInvoice(inv)} onContextMenu={(e) => handleRightClick(e, 'invoice', inv._id)} className="px-4 py-3 cursor-pointer active:bg-gray-800/40">
              <div className="flex justify-between items-start gap-2">
                <span className="font-semibold text-sm text-zinc-100 break-all">{inv.number}</span>
                <span className="text-green-400 font-bold text-sm shrink-0">${inv.total ? inv.total.toLocaleString() : '—'}</span>
              </div>
              <div className="flex justify-between items-center mt-2 gap-2">
                <select value={inv.status} onClick={(e) => e.stopPropagation()} onChange={(e) => handleStatusChange('invoice', inv._id, e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs font-medium focus:border-indigo-500 outline-none">
                  <option value="Draft">Draft</option><option value="Sent">Sent</option><option value="Viewed">Viewed</option><option value="Paid">Paid</option><option value="Overdue">Overdue</option>
                </select>
                <span className="text-zinc-500 text-xs flex items-center gap-2">
                  <span>Sent: <InvDateCell inv={inv} field="sentAt" onRefresh={onRefresh} /></span>
                  <span>Paid: <InvDateCell inv={inv} field="paidAt" onRefresh={onRefresh} /></span>
                </span>
              </div>
              {inv.status === 'Paid' && !receipts.some(r => String(r.invoice?._id || r.invoice) === String(inv._id)) && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/billing/create-receipt?project=${projectId}&invoice=${inv._id}`); }}
                  className="mt-2 text-xs font-bold px-3 py-1.5 rounded-lg bg-emerald-600/20 text-emerald-300 active:bg-emerald-600/35"
                >
                  + Create Receipt
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center">
          <h2 className="text-xl font-bold">Quotes</h2>
          <button onClick={() => navigate(`/billing/create-quote?project=${projectId}`)} className={createBtn}>
            <Plus size={16} /> Create Quote
          </button>
        </div>
        <div className="overflow-x-auto max-md:hidden">
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead className="bg-gray-950/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {quotes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-8 py-12 text-center text-zinc-500">No quotes yet</td>
                </tr>
              ) : (
                quotes.map((quote) => (
                  <tr
                    key={quote._id}
                    onClick={() => openQuote(quote)}
                    title={quote.status === 'Draft' ? 'Open draft to edit' : 'Open quote'}
                    className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                    onContextMenu={(e) => handleRightClick(e, 'quote', quote._id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{quote.number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                      ${quote.total ? quote.total.toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={quote.status}
                        onChange={(e) => handleStatusChange('quote', quote._id, e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs font-medium cursor-pointer focus:border-indigo-500 outline-none"
                      >
                        <option value="Draft">Draft</option>
                        <option value="Sent">Sent</option>
                        <option value="Viewed">Viewed</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Revised">Revised</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                      {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {invoiceForQuote(quote) ? (
                        // same pill as the bid drawer; here it also opens the invoice
                        <button
                          onClick={() => openInvoice(invoiceForQuote(quote))}
                          title={`Open ${invoiceForQuote(quote).number}`}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300"
                        >
                          Invoice Created
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/billing/create-invoice?fromQuote=${quote._id}`)}
                          title="Create an invoice prefilled from this quote"
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-600/25 text-indigo-300 hover:bg-indigo-600/40"
                        >
                          + Create Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Quotes — mobile cards */}
        <div className="md:hidden divide-y divide-gray-800/40">
          {quotes.length === 0 ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm">No quotes yet</p>
          ) : quotes.map((quote) => (
            <div key={quote._id} onClick={() => openQuote(quote)} onContextMenu={(e) => handleRightClick(e, 'quote', quote._id)} className="px-4 py-3 cursor-pointer active:bg-gray-800/40">
              <div className="flex justify-between items-start gap-2">
                <span className="font-semibold text-sm text-zinc-100 break-all">{quote.number}</span>
                <span className="text-green-400 font-bold text-sm shrink-0">${quote.total ? quote.total.toLocaleString() : '—'}</span>
              </div>
              <div className="flex justify-between items-center mt-2 gap-2">
                <select value={quote.status} onClick={(e) => e.stopPropagation()} onChange={(e) => handleStatusChange('quote', quote._id, e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs font-medium focus:border-indigo-500 outline-none">
                  <option value="Draft">Draft</option><option value="Sent">Sent</option><option value="Viewed">Viewed</option><option value="Accepted">Accepted</option><option value="Rejected">Rejected</option><option value="Revised">Revised</option>
                </select>
                <span className="text-zinc-500 text-xs">{quote.createdAt ? new Date(quote.createdAt).toLocaleDateString() : '—'}</span>
              </div>
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                {invoiceForQuote(quote) ? (
                  <button onClick={() => openInvoice(invoiceForQuote(quote))} title={`Open ${invoiceForQuote(quote).number}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Invoice Created</button>
                ) : (
                  <button onClick={() => navigate(`/billing/create-invoice?fromQuote=${quote._id}`)} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-600/25 text-indigo-300 active:bg-indigo-600/40">+ Create Invoice</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Change Orders Table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center">
          <h2 className="text-xl font-bold">Change Orders</h2>
          <button onClick={() => navigate(`/billing/create-change-order?project=${projectId}`)} className={createBtn}>
            <Plus size={16} /> Create CO
          </button>
        </div>
        <div className="overflow-x-auto max-md:hidden">
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead className="bg-gray-950/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Reason</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {!changeOrders || changeOrders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-8 py-12 text-center text-zinc-500">No change orders yet.</td>
                </tr>
              ) : (
                changeOrders.map((co) => (
                  <tr
                    key={co._id}
                    className="hover:bg-gray-800/50 transition-colors cursor-context-menu"
                    onContextMenu={(e) => handleRightClick(e, 'changeorder', co._id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{co.number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                      ${co.total ? co.total.toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-300">{co.reason || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <span className="px-2 py-1 bg-gray-800 rounded text-[10px] font-bold uppercase tracking-wider">
                         {co.status}
                       </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                      {co.createdAt ? new Date(co.createdAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Change Orders — mobile cards */}
        <div className="md:hidden divide-y divide-gray-800/40">
          {!changeOrders || changeOrders.length === 0 ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm">No change orders yet.</p>
          ) : changeOrders.map((co) => (
            <div key={co._id} onContextMenu={(e) => handleRightClick(e, 'changeorder', co._id)} className="px-4 py-3">
              <div className="flex justify-between items-start gap-2">
                <span className="font-semibold text-sm text-zinc-100 break-all">{co.number}</span>
                <span className="text-green-400 font-bold text-sm shrink-0">${co.total ? co.total.toLocaleString() : '—'}</span>
              </div>
              {co.reason && <p className="text-zinc-400 text-xs mt-1 break-words">{co.reason}</p>}
              <div className="flex justify-between items-center mt-2 gap-2">
                <span className="px-2 py-1 bg-gray-800 rounded text-[10px] font-bold uppercase tracking-wider">{co.status}</span>
                <span className="text-zinc-500 text-xs">{co.createdAt ? new Date(co.createdAt).toLocaleDateString() : '—'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ==================== INFORMAL ESTIMATE LOG ====================
          Prices quoted over text/phone, on the record. Affects NO totals. */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Informal Estimate Log Notes</h2>
            <p className="text-xs text-zinc-500 mt-1">Notes from informal agreements — nothing formal, never counted in totals</p>
          </div>
          <button
            onClick={() => setShowIeForm(v => !v)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-medium transition-all"
          >
            <Plus size={18} /> Log Estimate
          </button>
        </div>

        {/* Log Estimate modal — same look as Add Manual Payment */}
        {showIeForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-md border border-gray-700 shadow-2xl">
              <h2 className="text-2xl font-bold mb-1 text-white">Log Informal Estimate</h2>
              <p className="text-zinc-500 text-sm mb-6">Record only — never counted in totals</p>

              <div className="space-y-5">
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">What did you quote?</label>
                  <input
                    autoFocus
                    value={ieDesc}
                    onChange={(e) => setIeDesc(e.target.value)}
                    placeholder="e.g. panel swap + two circuits"
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Amount Quoted</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <input
                      value={ieAmount}
                      onChange={(e) => setIeAmount(e.target.value.replace(/[^0-9.-]/g, ''))}
                      placeholder="0.00"
                      inputMode="decimal"
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-8 pr-4 py-3 text-white focus:border-indigo-500 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Notes</label>
                  <textarea
                    value={ieNotes}
                    onChange={(e) => setIeNotes(e.target.value)}
                    placeholder="e.g. texted 9/7, wants it done with phase 2..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none h-24 resize-none transition-colors"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setShowIeForm(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addInformalEstimate}
                  disabled={savingIe || !ieDesc.trim() || ieAmount === ''}
                  className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl font-semibold transition-colors"
                >
                  {savingIe ? 'Logging…' : 'Log Estimate'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto max-md:hidden">
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead className="bg-gray-950/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Quoted</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Agreed</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Fully Paid</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Notes</th>
                <th className="px-6 py-4 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {informalEstimates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-8 py-12 text-center text-zinc-500">
                    Nothing logged yet. Quoted a price over text? Hit "Log Estimate" so it's on the record.
                  </td>
                </tr>
              ) : (
                informalEstimates.map((ie) => (
                  <tr
                    key={ie._id}
                    onContextMenu={(e) => handleRightClick(e, 'informalestimate', ie._id)}
                    className="hover:bg-gray-800/50 transition-colors group cursor-context-menu"
                  >
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${ie.fullyPaid ? 'text-zinc-600' : 'text-zinc-400'}`}>
                      {ie.createdAt ? new Date(ie.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td
                      onDoubleClick={() => startIeEdit(ie, 'description')}
                      title="Double-click to edit"
                      className={`px-6 py-4 text-sm ${ie.fullyPaid ? 'text-zinc-600' : 'text-zinc-300'}`}
                    >
                      {IeCell(ie, 'description', ie.description)}
                    </td>
                    <td
                      onDoubleClick={() => startIeEdit(ie, 'amount')}
                      title="Double-click to edit"
                      className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${ie.fullyPaid ? 'text-zinc-600' : 'text-green-400'}`}
                    >
                      {IeCell(ie, 'amount', <>${Number(ie.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>)}
                    </td>
                    <td className="px-6 py-4"><IeAgreedMark ie={ie} /></td>
                    <td className="px-6 py-4"><IePaidMark ie={ie} /></td>
                    <td
                      onDoubleClick={() => startIeEdit(ie, 'notes')}
                      className={`px-6 py-4 text-sm max-w-[220px] truncate ${ie.fullyPaid ? 'text-zinc-600' : 'text-zinc-400'}`}
                      title={ie.notes ? `${ie.notes} — double-click to edit` : 'Double-click to edit'}
                    >
                      {IeCell(ie, 'notes', ie.notes || '—')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => deleteInformalEstimate(ie)}
                        className="text-zinc-700 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                        title="Delete this log entry"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {informalEstimates.length > 0 && (
              <tfoot>
                <tr className="border-t border-gray-800 bg-gray-950/30">
                  <td className="px-6 py-4 text-sm font-semibold text-zinc-300">Estimated total amount</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-400">
                    ${informalEstimates.reduce((s, ie) => s + (Number(ie.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td colSpan={5} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Informal estimates — mobile cards */}
        <div className="md:hidden divide-y divide-gray-800/40">
          {informalEstimates.length === 0 ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm">Nothing logged yet. Tap "Log Estimate" to put a texted price on the record.</p>
          ) : informalEstimates.map((ie) => (
            <React.Fragment key={ie._id}>
            {SwipeWrap(`ie:${ie._id}`, () => deleteInformalEstimate(ie),
            <div
              onPointerDown={(e) => trackSwipe(`ie:${ie._id}`, e)}
              style={{ touchAction: 'pan-y' }}
              className="px-4 py-3"
            >
              <div className="flex justify-between items-start gap-2">
                <span
                  onClick={() => handleIeCellTap(ie, 'description')}
                  className={`text-sm break-words min-w-0 flex-1 ${ie.fullyPaid ? 'text-zinc-600' : 'text-zinc-200'}`}
                >
                  {IeCell(ie, 'description', ie.description)}
                </span>
                <span
                  onClick={() => handleIeCellTap(ie, 'amount')}
                  className={`font-bold text-sm shrink-0 ${ieEdit && ieEdit.id === ie._id && ieEdit.field === 'amount' ? 'w-24' : ''} ${ie.fullyPaid ? 'text-zinc-600' : 'text-green-400'}`}
                >
                  {IeCell(ie, 'amount', <>${Number(ie.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>)}
                </span>
              </div>
              <p
                onClick={() => handleIeCellTap(ie, 'notes')}
                className={`text-xs mt-1 break-words ${ie.fullyPaid ? 'text-zinc-600' : 'text-zinc-500'}`}
              >
                {IeCell(ie, 'notes', ie.notes || '—')}
              </p>
              <div className="flex justify-between items-center mt-2 gap-2">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <IeAgreedMark ie={ie} />
                    <span className="text-zinc-500 text-xs">Agreed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <IePaidMark ie={ie} />
                    <span className="text-zinc-500 text-xs">Fully Paid</span>
                  </div>
                </div>
                <span className="text-zinc-500 text-xs">{ie.createdAt ? new Date(ie.createdAt).toLocaleDateString() : '—'}</span>
              </div>
            </div>
            )}
            </React.Fragment>
          ))}
          {informalEstimates.length > 0 && (
            <div className="px-4 py-3 flex justify-between items-center bg-gray-950/30 border-t border-gray-800">
              <span className="text-sm font-semibold text-zinc-300">Estimated total amount</span>
              <span className="text-sm font-bold text-green-400">
                ${informalEstimates.reduce((s, ie) => s + (Number(ie.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Payments Received Table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center">
          <h2 className="text-xl font-bold">Payments Received</h2>
          <button
  onClick={() => setShowAddPaymentModal(true)}
  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-medium transition-all"
>
  <Plus size={18} /> Add Payment
</button>
        </div>
        <div className="overflow-x-auto max-md:hidden">
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead className="bg-gray-950/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Method</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Invoice</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {payments.length === 0 && invoices.filter(i => i.status === 'Paid').length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-8 py-12 text-center text-zinc-500">
                    No payments recorded yet. Click + to add manual payment.
                  </td>
                </tr>
              ) : (
                <>
                  {/* Automatic Invoice Payments (Read-only as they rely on Invoice Status) */}
                  {paidInvoicesToShow.map((inv, idx) => (
                    <tr key={`paid-${idx}`} onClick={() => openInvoice(inv)} title="Open invoice" className="hover:bg-gray-800/50 transition-colors italic opacity-80 cursor-pointer">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {inv.paidDate || inv.createdAt ? new Date(inv.paidDate || inv.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        ${inv.total ? inv.total.toLocaleString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Invoice Payment</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.number}</td>
                      <td className="px-6 py-4 text-sm text-zinc-500">—</td>
                    </tr>
                  ))}

                  {/* Manual Payments (Deleteable) */}
                  {payments.map((pay) => (
                    <tr
                      key={pay._id}
                      onClick={() => { const l = paymentReceiptLink(pay); if (l) navigate(l); }}
                      title={paymentReceiptLink(pay) ? 'Open the receipt for this payment' : undefined}
                      className={`hover:bg-gray-800/50 transition-colors ${paymentReceiptLink(pay) ? 'cursor-pointer' : 'cursor-context-menu'}`}
                      onContextMenu={(e) => handleRightClick(e, 'payment', pay._id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
  {new Date(pay.date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400 font-medium">
                        ${Number(pay.amount).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-300">{pay.method}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">{pay.invoice && pay.invoice !== '—' ? pay.invoice : '—'}</td>
                      <td className="px-6 py-4 text-sm text-zinc-400 max-w-[240px] truncate" title={pay.notes || ''}>{pay.notes || '—'}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        {/* Payments — mobile cards */}
        <div className="md:hidden divide-y divide-gray-800/40">
          {payments.length === 0 && invoices.filter(i => i.status === 'Paid').length === 0 ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm">No payments yet. Tap “Add Payment” above.</p>
          ) : (
            <>
              {paidInvoicesToShow.map((inv, idx) => (
                <div key={`paid-${idx}`} onClick={() => openInvoice(inv)} className="px-4 py-3 italic opacity-80 cursor-pointer active:bg-gray-800/40">
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-sm text-zinc-200 break-all">Invoice Payment · {inv.number}</span>
                    <span className="text-green-400 font-bold text-sm shrink-0">${inv.total ? inv.total.toLocaleString() : '—'}</span>
                  </div>
                  <span className="text-zinc-500 text-xs">{inv.paidDate || inv.createdAt ? new Date(inv.paidDate || inv.createdAt).toLocaleDateString() : '—'}</span>
                </div>
              ))}
              {payments.map((pay) => (
                <div key={pay._id} onClick={() => { const l = paymentReceiptLink(pay); if (l) navigate(l); }} onContextMenu={(e) => handleRightClick(e, 'payment', pay._id)} className={`px-4 py-3 ${paymentReceiptLink(pay) ? 'cursor-pointer active:bg-gray-800/40' : ''}`}>
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-sm text-zinc-200">{pay.method}</span>
                    <span className="text-green-400 font-bold text-sm shrink-0">${Number(pay.amount).toLocaleString()}</span>
                  </div>
                  <span className="text-zinc-500 text-xs">{new Date(pay.date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</span>
                  {pay.notes && <p className="text-zinc-400 text-xs mt-1 break-words">{pay.notes}</p>}
                </div>
              ))}
            </>
          )}
        </div>

  {/* ====================== ADD PAYMENT MODAL ====================== */}
        {showAddPaymentModal && newPayment && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-md border border-gray-700 shadow-2xl">
                <h2 className="text-2xl font-bold mb-6 text-white">Add Manual Payment</h2>
                
                <div className="space-y-5">
                <div>
                    <label className="block text-zinc-400 text-sm mb-2">Payment Date</label>
                        <input 
                        type="date" 
                        value={newPayment.date} 
                        onChange={(e) => {
                            setNewPayment({...newPayment, date: e.target.value});
                        }}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none transition-colors" 
                        />
                </div>

                <div>
                    <label className="block text-zinc-400 text-sm mb-2">Amount Received</label>
                    <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <input 
                        type="number" 
                        placeholder="0.00"
                        value={newPayment.amount} 
                        onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-8 pr-4 py-3 text-white focus:border-indigo-500 outline-none transition-colors" 
                    />
                    </div>
                </div>

                <div>
                    <label className="block text-zinc-400 text-sm mb-2">Payment Method</label>
                    <select 
                    value={newPayment.method} 
                    onChange={(e) => setNewPayment({...newPayment, method: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none transition-colors"
                    >
                    <option value="Check">Check</option>
                    <option value="Zelle">Zelle</option>
                    <option value="ACH / Wire">ACH / Wire</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Cash">Cash</option>
                    </select>
                </div>

                <div>
                    <label className="block text-zinc-400 text-sm mb-2">Notes</label>
                    <textarea 
                    value={newPayment.notes} 
                    onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none h-24 resize-none transition-colors"
                    placeholder="Check # or transaction reference..."
                    />
                </div>
                </div>

                <div className="flex gap-4 mt-8">
                <button 
                    onClick={() => setShowAddPaymentModal(false)}
                    className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
                >
                    Cancel
                </button>
                <button 
                    onClick={handleAddPayment}
                    className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold shadow-lg shadow-indigo-900/20 transition-all"
                >
                    Record Payment
                </button>
                </div>
            </div>
            </div>
        )}
        </div>

      {/* Receipts Table — every receipt made for this project */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Receipts</h2>
            <p className="text-xs text-zinc-500 mt-1">Proof-of-payment receipts made for this project — click one to reopen or resend it</p>
          </div>
          <button onClick={() => navigate(`/billing/create-receipt?project=${projectId}`)} className={createBtn}>
            <Plus size={16} /> Create Receipt
          </button>
        </div>
        <div className="overflow-x-auto max-md:hidden">
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead className="bg-gray-950/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Method</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Invoice</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {receipts.length === 0 ? (
                <tr><td colSpan={5} className="px-8 py-12 text-center text-zinc-500">No receipts yet — create one from a paid invoice above.</td></tr>
              ) : receipts.map((r) => (
                <tr key={r._id} onClick={() => navigate(`/billing/create-receipt?id=${r._id}`)} title="Open this receipt"
                  className="hover:bg-gray-800/50 transition-colors cursor-pointer">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{r.number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">${Number(r.amount || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-300">{r.method || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">{r.invoice?.number || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Receipts — mobile cards */}
        <div className="md:hidden divide-y divide-gray-800/40">
          {receipts.length === 0 ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm">No receipts yet — create one from a paid invoice above.</p>
          ) : receipts.map((r) => (
            <div key={r._id} onClick={() => navigate(`/billing/create-receipt?id=${r._id}`)} className="px-4 py-3 cursor-pointer active:bg-gray-800/40">
              <div className="flex justify-between items-start gap-2">
                <span className="font-semibold text-sm text-zinc-100 break-all">{r.number}</span>
                <span className="text-green-400 font-bold text-sm shrink-0">${Number(r.amount || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center mt-1 gap-2 text-xs text-zinc-500">
                <span>{r.method || '—'}{r.invoice?.number ? ` · ${r.invoice.number}` : ''}</span>
                <span>{r.date ? new Date(r.date).toLocaleDateString() : '—'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default RevenueTab;